import React from 'react';
import { PROFILE_PAGES } from './constants';
import ErrorFeedbackBar from '../global/ErrorFeedbackBar';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import { statusSelector } from '../../redux/selectors/global';
import { userInfoSelector } from '../../redux/selectors/auth';
import withShipment from '../../withShipment';
import AllLocationPlan from './AllLocationPlan';
import { availableCompaniesSelector } from '../../redux/selectors/account';
import { listAvailableCompanies } from '../../redux/actions/settings';
import LocationDetailView from './LocationDetailView';
import { fetchLocationDetail } from '../../redux/actions/userManagement';
import { refreshUserInfo } from '../../redux/actions/auth';
import { locationDetailSelector } from '../../redux/selectors/userManagement';
import CustomToolbar from '../global/CustomToolbar';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import Paper from '@material-ui/core/Paper';

class UserProfile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: PROFILE_PAGES.LOCATION_PLAN,
      feedbackBarOpen: false,
      error: false,
      feedbackStatus: '',
      feedbackMessage: '',
      selectedLocation: null,
    };
  }

  // Fires the deep-link at most once per mount. Without it, pressing
  // Back from the detail view would bounce straight back into it on the
  // next render.
  deepLinkHandled = false;

  componentDidMount() {
    this.props.listAvailableCompanies();
    // Header already fetches the company list on every authenticated page,
    // so in a live session the list is in the store before this page
    // mounts and the "just populated" path in componentDidUpdate never
    // fires. Try here first; that path only covers a cold load.
    this.tryDeepLink();
  }

  // Detects ?tab=subscription (or any tab=…) on the URL and auto-opens
  // the user's location detail view once the companies list has loaded.
  // Used by the trial-grace banner button and any external link that
  // wants to drop the user straight into their subscription screen
  // without making them click through the location picker.
  componentDidUpdate(prevProps, prevState) {
    // First time availableCompanies populates after mount, check the
    // query string and auto-navigate if the URL asked for a deep-link.
    const justLoaded =
      (!prevProps.availableCompanies ||
        prevProps.availableCompanies.length === 0) &&
      this.props.availableCompanies &&
      this.props.availableCompanies.length > 0;

    if (
      justLoaded &&
      this.state.currentPage === PROFILE_PAGES.LOCATION_PLAN &&
      !this.state.selectedLocation
    ) {
      this.tryDeepLink();
    }

    // The banner's Pay Now button lives in the app shell, so it can be
    // clicked while this page is already open — React Router hands us a
    // new query string without remounting, and componentDidMount won't
    // run again. Re-arm on a real query change so the jump still lands.
    if (prevProps.location?.search !== this.props.location?.search) {
      this.deepLinkHandled = false;
      this.tryDeepLink();
    }

    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar(this.props.status.statusText, 'error');
    }
  }

  // Reads ?tab=… from the current URL and jumps straight to the
  // LocationDetailView for the location the user is currently working in.
  // The default tab in that view is already 'subscription', so
  // /profile?tab=subscription lands exactly where the user expects —
  // that's what the trial-grace banner's Pay Now button relies on.
  // Multi-location admins get their SELECTED location, not the picker:
  // the location they're switched into is the one whose subscription
  // they mean. Falls back to the picker when it can't be resolved.
  tryDeepLink = () => {
    if (this.deepLinkHandled) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (!tabParam) return;

      const companies = this.props.availableCompanies || [];
      // Nothing to match against yet — componentDidUpdate retries once
      // the list lands.
      if (companies.length === 0) return;

      // selectedLocationId tracks the header company picker; `company` is
      // the home company, used only for accounts that never switched.
      const targetId =
        this.props.user?.selectedLocationId || this.props.user?.company;
      let target = targetId
        ? companies.find((c) => c.companyId === targetId) || null
        : null;
      if (!target && companies.length === 1) {
        target = companies[0];
      }
      if (!target) return;

      this.deepLinkHandled = true;
      this.handleOpenLocation(target);
    } catch (e) {
      // best-effort deep-link; on any parse error fall back to the
      // standard picker UX.
    }
  };

  // Sends the user to Settings > Manage Employees, recording where they came
  // from so that page can offer a Back control. Without the return path they'd
  // have to walk back through the location picker to reach the Team tab again.
  handleManageEmployees = () => {
    this.props.history.push('/settings/manage-users', {
      from: '/profile?tab=team',
      fromLabel: 'Back to My Account',
    });
  };

  handleBack = () => {
    this.setState({ selectedLocation: null }, () => {
      this.setCurrentPage(PROFILE_PAGES.LOCATION_PLAN, {});
    });
  };

  handleOpenLocation = (location) => {
    this.setState({ selectedLocation: location }, () => {
      this.props.fetchLocationDetail({ locationId: location.companyId });
      this.setCurrentPage(PROFILE_PAGES.LOCATION_DETAIL_VIEW, {});
    });
  };

  renderAllLocation = () => {
    return (
      <AllLocationPlan
        user={this.props.user}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        availableCompanies={this.props.availableCompanies}
        handleOpenLocation={this.handleOpenLocation}
      />
    );
  };

  renderDetailView = () => {
    return (
      <LocationDetailView
        user={this.props.user}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        location={this.state.selectedLocation}
        handleBack={this.handleBack}
        locationDetail={this.props.locationDetail}
        fetchLocationDetail={this.props.fetchLocationDetail}
        refreshUserInfo={this.props.refreshUserInfo}
        onManageEmployees={
          (this.props.user?.routes || []).includes('manage-users')
            ? this.handleManageEmployees
            : null
        }
      />
    );
  };

  setCurrentPage = (newPage) => {
    this.setState({
      currentPage: newPage,
    });
  };

  handleDeleteRow = (deleteId) => {
    this.props.deleteManageBonus({ manageBonusId: deleteId });
  };

  displayCurrentPage = () => {
    switch (this.state.currentPage) {
      case PROFILE_PAGES.LOCATION_PLAN:
        return this.renderAllLocation();
      case PROFILE_PAGES.LOCATION_DETAIL_VIEW:
        return this.renderDetailView();
      default:
        return <div>We encountered an error. Please refresh the page.</div>;
    }
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  render() {
    const isDetail =
      this.state.currentPage === PROFILE_PAGES.LOCATION_DETAIL_VIEW;
    const title = 'My Account';

    const headerButtons = isDetail
      ? [
          {
            icon: <KeyboardBackspaceIcon />,
            handler: this.handleBack,
            title: 'Back',
          },
        ]
      : [];

    return (
      <React.Fragment>
        <Paper className="paper">
          <ErrorFeedbackBar />
          <FeedbackBar
            open={this.state.feedbackBarOpen}
            handleClose={this.closeFeedbackBar}
            severity={this.state.feedbackStatus}
            message={this.state.feedbackMessage}
          />
          <CustomToolbar title={title} headerButtons={headerButtons} />
          {this.displayCurrentPage()}
        </Paper>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  status: statusSelector(state),
  user: userInfoSelector(state),
  availableCompanies: availableCompaniesSelector(state),
  locationDetail: locationDetailSelector(state),
});

const actionCreators = {
  listAvailableCompanies,
  fetchLocationDetail,
  refreshUserInfo,
};

export default withShipment(
  {
    actionCreators,
    mapStateToProps,
  },
  UserProfile
);
